import React, { useState, useCallback } from 'react';
import {
  Lightbulb,
  X,
  Send,
  Loader2,
  CheckCircle,
  ChevronUp,
} from 'lucide-react';
import { useIngest } from '../hooks/useIngest';

function InsightsPanel() {
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [content, setContent] = useState('');
  const [status, setStatus] = useState('idle'); // idle, processing, success, error
  const [result, setResult] = useState(null);

  const { submitInsight, loading } = useIngest();

  const handleSubmit = useCallback(async () => {
    if (!content.trim()) return;

    setStatus('processing');

    // Simulate processing
    await new Promise(resolve => setTimeout(resolve, 1500));

    // Simulate success
    setStatus('success');
    setResult({
      entities_created: 1,
      relationships_created: 2,
    });

    // Reset after delay
    setTimeout(() => {
      setContent('');
      setStatus('idle');
      setResult(null);
    }, 3000);
  }, [content]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && e.metaKey) {
      handleSubmit();
    }
  }, [handleSubmit]);

  // Collapsed floating button
  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 bg-accent-purple hover:bg-accent-purple-dark text-white px-4 py-3 rounded-full shadow-lg flex items-center gap-2 transition-all hover:scale-105 z-50"
      >
        <Lightbulb size={20} />
        <span className="font-medium">Insights</span>
      </button>
    );
  }

  return (
    <div
      className={`fixed bottom-6 right-6 bg-dark-800 border border-dark-600 rounded-lg shadow-xl z-50 transition-all ${
        isExpanded ? 'w-96' : 'w-80'
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-dark-600">
        <div className="flex items-center gap-2">
          <Lightbulb size={18} className="text-accent-purple" />
          <span className="font-semibold text-sm">Quick Insight</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1.5 hover:bg-dark-700 rounded transition-colors"
          >
            <ChevronUp
              size={16}
              className={`transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            />
          </button>
          <button
            onClick={() => {
              setIsOpen(false);
              setContent('');
              setStatus('idle');
            }}
            className="p-1.5 hover:bg-dark-700 rounded transition-colors"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-3">
        {status === 'idle' && (
          <>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Paste content or type an insight...

Example: 'payment-service now uses circuit breaker pattern for external calls'"
              className={`w-full bg-dark-700 border border-dark-600 rounded-lg p-3 text-sm resize-none focus:outline-none focus:border-accent-purple transition-colors ${
                isExpanded ? 'h-48' : 'h-24'
              }`}
            />
            <div className="flex items-center justify-between mt-3">
              <span className="text-xs text-dark-400">
                ⌘ + Enter to submit
              </span>
              <button
                onClick={handleSubmit}
                disabled={!content.trim()}
                className="btn-primary text-sm px-4 py-1.5 flex items-center gap-2 disabled:opacity-50"
              >
                <Send size={14} />
                Add to AOI
              </button>
            </div>
          </>
        )}

        {status === 'processing' && (
          <div className="flex flex-col items-center justify-center py-8">
            <Loader2 size={32} className="animate-spin text-accent-purple mb-3" />
            <p className="text-sm text-dark-300">Extracting knowledge...</p>
          </div>
        )}

        {status === 'success' && (
          <div className="flex flex-col items-center justify-center py-8">
            <CheckCircle size={32} className="text-green-400 mb-3" />
            <p className="text-sm font-medium mb-1">Added to AOI!</p>
            {result && (
              <p className="text-xs text-dark-400">
                {result.entities_created} entities, {result.relationships_created} relationships
              </p>
            )}
          </div>
        )}

        {status === 'error' && (
          <div className="flex flex-col items-center justify-center py-8">
            <X size={32} className="text-red-400 mb-3" />
            <p className="text-sm text-red-400">Failed to process insight</p>
            <button
              onClick={() => setStatus('idle')}
              className="mt-3 text-xs text-accent-purple hover:text-accent-purple-light"
            >
              Try again
            </button>
          </div>
        )}
      </div>

      {/* Examples */}
      {status === 'idle' && !content && (
        <div className="px-3 pb-3">
          <p className="text-xs text-dark-400 mb-2">Quick examples:</p>
          <div className="flex flex-wrap gap-1">
            {[
              'auth-service uses OAuth 2.0',
              'loans depends on payments',
              'Platform team owns auth',
            ].map((example, i) => (
              <button
                key={i}
                onClick={() => setContent(example)}
                className="text-xs px-2 py-1 bg-dark-700 hover:bg-dark-600 rounded text-dark-300 hover:text-white transition-colors"
              >
                {example}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default InsightsPanel;
